import { Button, Box } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import ClearIcon from "@mui/icons-material/Clear";
import ShareIcon from "@mui/icons-material/Share";

function CameraCapture() {
  const canvas = document.createElement("canvas");
  const destCtx = canvas.getContext("2d");
  const [imgeUrl, setImageUrl] = useState(null);
  const [resumeVisible, setResumeVisible] = useState(false);
  const heroRef = useRef(null);

  useEffect(() => {
    const container = document.getElementById("container");
    if (container)
      container.classList.toggle("parallax-receded", resumeVisible);
  }, [resumeVisible]);

  // subtle independent float for the hero text - a much smaller offset than
  // the face-tracked depth parallax driving the background behind it
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const STRENGTH = -10;
    let tx = 0,
      ty = 0,
      mx = 0,
      my = 0,
      frame;

    const onPointerMove = (e) => {
      tx = (e.clientX / window.innerWidth - 0.5) * 2;
      ty = (e.clientY / window.innerHeight - 0.5) * 2;
    };
    const onTilt = (e) => {
      if (e.gamma == null) return;
      tx = Math.max(-1, Math.min(1, e.gamma / 35));
      ty = Math.max(-1, Math.min(1, (e.beta - 45) / 35));
    };

    window.addEventListener("pointermove", onPointerMove, { passive: true });
    window.addEventListener("deviceorientation", onTilt);

    const loop = () => {
      mx += (tx - mx) * 0.05;
      my += (ty - my) * 0.05;
      if (heroRef.current) {
        heroRef.current.style.transform = `translate3d(${mx * STRENGTH}px, ${
          my * STRENGTH
        }px, 0)`;
      }
      frame = requestAnimationFrame(loop);
    };
    loop();

    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("deviceorientation", onTilt);
      cancelAnimationFrame(frame);
    };
  }, []);

  const shareHandler = async () => {
    const fileName = "share-image.png";
    const shareFileBlob = await fetch(imgeUrl).then((res) => res.blob());
    const shareData = {
      files: [
        new File([shareFileBlob], fileName, {
          type: shareFileBlob.type,
        }),
      ],
      title: "Jason Morris, XR/AI Engineer",
      text: `Jason Morris, XR/AI Engineer`,
    };

    try {
      await navigator.share(shareData);
      console.log("Data was shared successfully");
    } catch (err) {
      console.log(err.message);
      if (!err.toString().includes("AbortError")) {
        const link = document.createElement("a");
        link.href = imgeUrl;
        link.download = fileName;
        link.click();
      }
    }
  };

  const capture = async () => {
    const canvasC = document.querySelector("canvas");
    const video = document.querySelector("video");
    const node = document.getElementById("container");
    canvas.width = node.offsetWidth;
    canvas.height = node.offsetHeight;

    let scaleFactor = Math.max(
      canvas.width / video.width,
      canvas.height / video.height,
    );
    let newWidth = video.width * scaleFactor;
    let newHeight = video.height * scaleFactor;
    let x = canvas.width / 2 - newWidth / 2;
    let y = canvas.height / 2 - newHeight / 2;
    destCtx.drawImage(canvasC, x, y, newWidth, newHeight);
    const imageData = await canvas.toDataURL();
    setImageUrl(imageData);
  };

  return (
    <>
      {imgeUrl && (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            position: "absolute",
            top: "0",
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
            justifyContent: "center",
            backgroundColor: "#00000063",
            zIndex: "2000",
          }}
        >
          <Box
            sx={{
              padding: "20px",
              display: "flex",
              backgroundColor: "white",
              alignItems: "center",
              flexDirection: "column",
              justifyContent: "center",
              borderRadius: "15px",
              position: "relative",
              gap: "10px",
            }}
          >
            <ClearIcon
              onClick={() => {
                setImageUrl(null);
              }}
              sx={{
                right: "0",
                color: "#b102ed",
                width: "35px",
                margin: "2px",
                height: "35px",
                borderRadius: "100px",
                padding: "2px",
                border: "2px solid #b102ed",
                alignSelf: "end",
              }}
            />
            <Box
              component='img'
              src={imgeUrl}
              sx={{
                maxWidth: "260px",
              }}
            ></Box>
            <Box>
              <Button
                onClick={shareHandler}
                variant='contained'
                sx={{
                  color: "white",
                  background: "#b102ed",
                  "&:hover": {
                    background: "#b102ed",
                  },
                }}
                startIcon={<ShareIcon />}
              >
                Share
              </Button>
            </Box>
          </Box>
        </Box>
      )}
      {false && (
        <Button
          onClick={capture}
          sx={{
            position: "absolute",
            bottom: "20px",
            left: "calc(50% - 50px)",
            width: "100px",
            zIndex: "1000",
          }}
        >
          <Box
            component='img'
            src='./assets/images/photo-button-circle.png'
            sx={{
              width: "100%",
            }}
          ></Box>
        </Button>
      )}

      {
        <>
          <Box
            className={`scrim${resumeVisible ? " scrim--active" : ""}`}
            aria-hidden='true'
          />

          <header className='site-nav'>
            <a className='mark' href='#top'>
              Jason Morris <b>&middot;</b> VeeR Goggles
            </a>
            <nav>
              {resumeVisible && (
                <>
                  <ul>
                    <li>
                      <a href='#about'>About</a>
                    </li>
                    <li>
                      <a href='#work'>Work</a>
                    </li>
                    <li>
                      <a href='#recognition'>Recognition</a>
                    </li>
                    <li>
                      <a href='#contact'>Contact</a>
                    </li>
                  </ul>
                  <button
                    type='button'
                    className='cta-toggle'
                    onClick={() => setResumeVisible((visible) => !visible)}
                  >
                    <span>Hide Résumé</span>
                    <span className='cta-arrow'>↑</span>
                  </button>
                </>
              )}
            </nav>
          </header>

          <header
            className={`hero-stage${resumeVisible ? " hero-stage--hidden" : ""}`}
            aria-hidden={resumeVisible}
          >
            <div className='hero' ref={heroRef}>
              <div className='eyebrow'>
                VR &middot; AR &middot; Spatial &middot; since 2006
              </div>
              <h1>
                VeeR Goggles{" "}
                <span className='part-of-speech'>
                  <em>proper plural noun:</em>
                </span>
                <br />
                <span className='definition-index definition-1'>1:</span> a
                <em>VR/AR</em> consultancy
                <br />
                <span className='definition-index definition-2'>2:</span>{" "}
                digital <em>rose-colored glasses</em>
                <br />
                <span className='definition-index definition-3'>3:</span>{" "}
                screens that <em>move you</em>
                <br />
              </h1>
              <button
                type='button'
                className='cta-toggle hero-cta'
                onClick={() => setResumeVisible(true)}
              >
                <span>View Résumé</span>
                <span className='cta-arrow'>↓</span>
              </button>
            </div>
          </header>

          <div
            id='content'
            className={resumeVisible ? "is-visible" : "is-hidden"}
            aria-hidden={!resumeVisible}
          >
            <main id='top'>
              <section id='about'>
                <div className='eyebrow'>About</div>
                <div className='about-grid'>
                  <p className='lede'>
                    <b>I'm Jason Morris</b> - an XR engineer and creative
                    technologist. For twenty years I've turned flat images, live
                    data, and human stories into spaces you experience.
                  </p>
                  <p>
                    I founded <strong>VeeR Goggles</strong> as a personal XR
                    studio, and now specialize in medical R&D under{" "}
                    <strong>Insula&nbsp;XR</strong>. I'm lead application
                    developer on <strong>AngioPilot</strong>, a Mixed Reality
                    catheterization trainer on Apple Vision Pro, built alongside
                    practicing interventional radiologists for MedVR.
                  </p>
                  <p>
                    Before that: a decade of award-winning experiential R&amp;D
                    at Neo-Pangea, engineering leadership at Hilton, and a long
                    tail of WebXR experiments. Trained as a computer engineer
                    (B.S. &amp; M.S., University of Florida), with graduate work
                    in neural networks and expert systems, I was born for this
                    moment.
                  </p>
                </div>
              </section>

              <section id='work'>
                <div className='eyebrow'>Selected work</div>
                <div className='work-list'>
                  <a
                    className='work-item'
                    href='https://medvr.io'
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    <div className='work-yr'>2025</div>
                    <div className='work-main'>
                      <h3>
                        AngioPilot <span className='arrow'>↗</span>
                      </h3>
                      <p>
                        Mixed reality vascular catheterization training on Apple
                        Vision Pro, featuring real-time simulated fluoroscopy,
                        controller-free hand tracking, validated with
                        interventional radiologists.
                      </p>
                    </div>
                    <div className='tags'>
                      <span className='tag'>Lead Dev</span>
                      <span className='tag'>visionOS</span>
                      <span className='tag'>Swift</span>
                      <span className='tag'>RealityKit</span>
                    </div>
                  </a>

                  <a
                    className='work-item'
                    href='https://www.readingeagle.com/2015/11/01/neo-pangea-creates-a-breakthrough/'
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    <div className='work-yr'>2016</div>
                    <div className='work-main'>
                      <h3>
                        Breakthrough - National Geographic{" "}
                        <span className='arrow'>↗</span>
                      </h3>
                      <p>
                        An Emmy-nominated WebVR experience and winner of a 2016
                        SXSW Innovation Award, bringing the series' science to
                        life in the browser.
                      </p>
                    </div>
                    <div className='tags'>
                      <span className='tag'>Lead Dev</span>
                      <span className='tag'>WebVR</span>
                      <span className='tag'>SXSW</span>
                    </div>
                  </a>

                  <a
                    className='work-item'
                    href='https://devpost.com/software/garden-cards'
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    <div className='work-yr'>2019</div>
                    <div className='work-main'>
                      <h3>
                        gARden Cards <span className='arrow'>↗</span>
                      </h3>
                      <p>
                        Collaborative mixed-reality greeting cards - Best Mixed
                        Reality at MIT's Reality Virtually Hackathon.
                      </p>
                    </div>
                    <div className='tags'>
                      <span className='tag'>Lead Dev</span>
                      <span className='tag'>A-Frame</span>
                      <span className='tag'>Mixed Reality</span>
                    </div>
                  </a>

                  <a
                    className='work-item'
                    href='https://vimeo.com/manage/videos/167883639'
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    <div className='work-yr'>2016-2020</div>
                    <div className='work-main'>
                      <h3>
                        VeeR Into Experiences <span className='arrow'>↗</span>
                      </h3>
                      <p>
                        A standing body of WebXR - interactive games, training
                        simulations, museum exhibits, and immersive real-estate
                        tours. Includes a Host-driven shared VR experience
                        streamed live to multiple Guests, adopted for forensic
                        investigations and courtroom jury walkthroughs.
                      </p>
                    </div>
                    <div className='tags'>
                      <span className='tag'>Founder</span>
                      <span className='tag'>three.js</span>
                      <span className='tag'>WebXR</span>
                    </div>
                  </a>

                  <a
                    className='work-item'
                    href='https://github.com/VeerGoggles/parelax'
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    <div className='work-yr'>2023</div>
                    <div className='work-main'>
                      <h3>
                        pARelax <span className='arrow'>↗</span>
                      </h3>
                      <p>
                        A pose-based "virtual window" - tracking the viewer to
                        drive a camera through a depth-mapped scene. The
                        technique running quietly behind this page.
                      </p>
                    </div>
                    <div className='tags'>
                      <span className='tag'>UI/UX</span>
                      <span className='tag'>WebAR</span>
                      <span className='tag'>R3F</span>
                    </div>
                  </a>

                  <a
                    className='work-item'
                    href='https://www.neopangea.com/portfolio/walmart/your-zone-ar.html'
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    <div className='work-yr'>2009</div>
                    <div className='work-main'>
                      <h3>
                        Walmart YourZone AR <span className='arrow'>↗</span>
                      </h3>
                      <p>
                        One of the world's first commercial AR experiences -
                        letting readers of People magazine explore a product
                        room in augmented reality.
                      </p>
                    </div>
                    <div className='tags'>
                      <span className='tag'>Lead Dev</span>
                      <span className='tag'>WebAR</span>
                    </div>
                  </a>

                  <a
                    className='work-item'
                    href='https://www.internabuser.com/'
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    <div className='work-yr'>2012</div>
                    <div className='work-main'>
                      <h3>
                        Intern Abuser <span className='arrow'>↗</span>
                      </h3>
                      <p>
                        Internet teleoperation of robotic hardware with live
                        video - players worldwide controlled physical actuators
                        in real time. A 2013 SXSW Interactive finalist.
                      </p>
                    </div>
                    <div className='tags'>
                      <span className='tag'>Lead Dev</span>
                      <span className='tag'>Python</span>
                      <span className='tag'>Robotics</span>
                    </div>
                  </a>

                  <a
                    className='work-item'
                    href='https://play.google.com/store/apps/details?id=air.com.fxnetworks.thestrain.transformation'
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    <div className='work-yr'>2014</div>
                    <div className='work-main'>
                      <h3>
                        The Strain: Transformation{" "}
                        <span className='arrow'>↗</span>
                      </h3>
                      <p>
                        An iOS &amp; Android AR app for FX's hit series, using
                        3D facial recognition and real-time image manipulation.
                      </p>
                    </div>
                    <div className='tags'>
                      <span className='tag'>Lead Dev</span>
                      <span className='tag'>AR</span>
                    </div>
                  </a>
                </div>
              </section>

              <section id='recognition'>
                <div className='eyebrow'>Recognition</div>
                <div className='rec-list'>
                  <div className='rec-item'>
                    <div className='rec-award'>SXSW Innovation Award</div>
                    <div className='rec-detail'>
                      National Geographic &ldquo;Breakthrough&rdquo; &middot;
                      2016
                    </div>
                  </div>
                  <div className='rec-item'>
                    <div className='rec-award'>Emmy nominations &times;3</div>
                    <div className='rec-detail'>
                      Breakthrough &middot; Eat: The Story of Food &middot; Rise
                      of the Robots
                    </div>
                  </div>
                  <div className='rec-item'>
                    <div className='rec-award'>Best Mixed Reality</div>
                    <div className='rec-detail'>
                      MIT Reality Virtually Hackathon &middot; 2019
                    </div>
                  </div>
                  <div className='rec-item'>
                    <div className='rec-award'>Tools for Innovators</div>
                    <div className='rec-detail'>
                      MIT Reality Hack &middot; 2021
                    </div>
                  </div>
                  <div className='rec-item'>
                    <div className='rec-award'>SXSW Interactive Finalist</div>
                    <div className='rec-detail'>
                      Intern Abuser &middot; 2013
                    </div>
                  </div>
                </div>
              </section>

              <section className='contact' id='contact'>
                <div className='eyebrow'>Contact</div>
                <h2>
                  Make <em>Believers</em>
                </h2>
                <div className='contact-links'>
                  <a href='mailto:jason@veergoggles.com'>
                    <span className='lbl'>email</span> jason@veergoggles.com
                  </a>
                  <a href='tel:+14847977878'>
                    <span className='lbl'>phone</span>{" "}
                    484&middot;797&middot;7878
                  </a>
                  <a
                    href='https://www.linkedin.com/in/jason-morris-a803294'
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    <span className='lbl'>linkedin</span> /jason-morris
                  </a>
                  <a
                    href='https://github.com/VeerGoggles'
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    <span className='lbl'>github</span> /VeerGoggles
                  </a>
                </div>
              </section>
            </main>

            <footer>
              <p className='footer-note'>
                The background is depth-mapped photos of my father's early life,
                <br />
                a window into the youth that made him the man I knew.
                <br />
                In loving memory of William Paul Morris.
              </p>
              <p className='footer-meta'>
                &copy;2026 Jason Morris &middot; VeeR Goggles &middot; InsulaXR
              </p>
            </footer>
          </div>
        </>
      }
    </>
  );
}

export default CameraCapture;
